import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { HeartHandshake, Bell, Users2, FolderOpen, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

function Stat({ icon: Icon, label, value, testId, accent = "bg-accents-care" }) {
  return (
    <div className="rounded-3xl bg-white shadow-card p-6" data-testid={testId}>
      <div className="flex items-center gap-2 text-ink-500 text-[11px] font-semibold uppercase tracking-[0.15em]">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {label}
      </div>
      <div className="font-heading text-3xl text-ink-900 mt-2 tracking-tight">{value ?? "—"}</div>
    </div>
  );
}

export default function CareHome() {
  const t = useT();

  const me = useQuery({
    queryKey: ["care-me"],
    queryFn: async () => (await api.get("/care/me")).data,
    retry: false,
  });
  const clients = useQuery({
    queryKey: ["care-clients"],
    queryFn: async () => (await api.get("/care/clients")).data,
    retry: false,
  });
  const alerts = useQuery({
    queryKey: ["care-alerts"],
    queryFn: async () => (await api.get("/care/alerts")).data,
    retry: false,
  });

  const clientList = Array.isArray(clients.data) ? clients.data : clients.data?.items || clients.data?.clients || [];
  const alertList = Array.isArray(alerts.data) ? alerts.data : alerts.data?.items || alerts.data?.alerts || [];

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t("nav.care")}
        title={t("care.title")}
        subtitle={t("care.subtitle")}
      />

      {(clients.isLoading || alerts.isLoading) && <LoadingCard height="h-24" />}
      {clients.isError && <ErrorState message={errMsg(clients.error)} onRetry={() => clients.refetch()} />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Stat testId="care-stat-clients" icon={Users2} label={t("care.clients")} value={clientList.length || (me.data?.clients_count ?? "—")} />
        <Stat testId="care-stat-alerts" icon={Bell} label={t("care.alerts")} value={alertList.length} />
        <Stat testId="care-stat-dossier" icon={FolderOpen} label={t("care.dossier")} value={me.data?.dossier_count ?? "—"} />
      </div>

      <section>
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-heading text-xl text-ink-900">{t("care.alerts")}</h2>
        </div>
        {alertList.length === 0 && !alerts.isLoading && (
          <EmptyState icon={HeartHandshake} title={t("care.noAlerts")} testId="care-alerts-empty" />
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="care-alerts-list">
          {alertList.slice(0, 6).map((a, i) => (
            <div key={a.id || i} className="rounded-2xl bg-white shadow-card p-4 border-l-4 border-rust-500">
              <div className="text-xs text-ink-500">{a.created_at && new Date(a.created_at).toLocaleString()}</div>
              <div className="font-heading text-ink-900 mt-1">{a.client_name || a.name || "Client"}</div>
              <div className="text-sm text-ink-700 mt-1">{a.reason || a.message || a.text || ""}</div>
            </div>
          ))}
        </div>
      </section>

      <Link to="/app/care/clients" className="rounded-3xl bg-white shadow-card p-6 flex items-center gap-3 hover:shadow-card-hover transition" data-testid="care-open-clients">
        <div className="h-11 w-11 rounded-2xl bg-accents-care flex items-center justify-center">
          <Users2 className="h-5 w-5 text-white" strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <div className="font-heading text-lg text-ink-900">{t("care.clients")}</div>
          <div className="text-sm text-ink-500">{t("care.subtitle")}</div>
        </div>
        <ArrowRight className="h-4 w-4 text-ink-500" />
      </Link>
    </div>
  );
}
